/**
 * Som e notificação do sistema quando um agente para de escrever.
 *
 * A guia precisa continuar aberta: com ela fechada o navegador encerra a
 * página e não há o que tocar. Em segundo plano, a notificação do sistema
 * é o que atravessa; o som toca quando o navegador ainda deixa.
 */

import { useStore } from './store';
import { connection } from './ws';
import { QUIET_MS, createTurn, noteInput, noteOutput, noteQuiet, noteResize, type TurnState } from './turnSettle';

const STORAGE_KEY = 'cm.chime';

const turns = new Map<string, TurnState>();
const timers = new Map<string, number>();
let audio: AudioContext | null = null;
let askedPermission = false;

export function chimeEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setChimeEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* modo privado */
  }
  if (on) {
    resumeAudio();
    void requestNotifyPermission();
  }
}

function turnFor(sessionId: string): TurnState {
  let state = turns.get(sessionId);
  if (!state) {
    state = createTurn(Date.now());
    turns.set(sessionId, state);
  }
  return state;
}

function sessionLabel(sessionId: string): string {
  const session = useStore.getState().sessions.find((s) => s.id === sessionId);
  return session?.title?.trim() || 'Agente';
}

function resumeAudio(): void {
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return;
  if (!audio) audio = new Ctx();
  if (audio.state === 'suspended') void audio.resume();
}

async function requestNotifyPermission(): Promise<void> {
  if (askedPermission || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'default') return;
  askedPermission = true;
  try {
    await Notification.requestPermission();
  } catch {
    /* o navegador recusou o pedido */
  }
}

function playChime(): void {
  resumeAudio();
  if (!audio) return;
  const now = audio.currentTime;
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(784, now);
  osc.frequency.setValueAtTime(1046, now + 0.09);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.07, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
  osc.connect(gain);
  gain.connect(audio.destination);
  osc.start(now);
  osc.stop(now + 0.34);
}

function notify(sessionId: string): void {
  const away = document.hidden || !document.hasFocus();
  if (away && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      const note = new Notification(`${sessionLabel(sessionId)} respondeu`, {
        body: 'O agente terminou a resposta.',
        tag: `cm-chime-${sessionId}`,
        silent: false,
      });
      note.onclick = () => {
        window.focus();
        note.close();
      };
    } catch {
      /* notificação indisponível */
    }
  }
  playChime();
}

function schedule(sessionId: string): void {
  const pending = timers.get(sessionId);
  if (pending !== undefined) window.clearTimeout(pending);
  timers.set(
    sessionId,
    window.setTimeout(() => {
      timers.delete(sessionId);
      const state = turns.get(sessionId);
      if (!state) return;
      const result = noteQuiet(state, Date.now());
      turns.set(sessionId, result.state);
      if (result.settled && chimeEnabled()) notify(sessionId);
    }, QUIET_MS),
  );
}

export function observeOutput(sessionId: string, data: string): void {
  const next = noteOutput(turnFor(sessionId), data.length, Date.now());
  turns.set(sessionId, next);
  schedule(sessionId);
}

export function observeInput(sessionId: string, data: string): void {
  turns.set(sessionId, noteInput(turnFor(sessionId), data));
}

export function observeResize(sessionId: string): void {
  turns.set(sessionId, noteResize(turnFor(sessionId), Date.now()));
}

export function forgetTurn(sessionId: string): void {
  const pending = timers.get(sessionId);
  if (pending !== undefined) window.clearTimeout(pending);
  timers.delete(sessionId);
  turns.delete(sessionId);
}

/** Liga o detector na conexão. A guia aberta (mesmo em segundo plano) é o que escuta. */
export function installChime(): () => void {
  const offActivity = connection.onActivity((sessionId, data, isReplay) => {
    if (!isReplay) observeOutput(sessionId, data);
  });
  const offInput = connection.onInput((sessionId, data) => observeInput(sessionId, data));
  const offResize = connection.onResize((sessionId) => observeResize(sessionId));
  const offEnd = connection.onSessionEnd((sessionId) => forgetTurn(sessionId));
  const unlock = () => {
    if (!chimeEnabled()) return;
    resumeAudio();
    void requestNotifyPermission();
  };
  window.addEventListener('pointerdown', unlock);
  return () => {
    offActivity();
    offInput();
    offResize();
    offEnd();
    window.removeEventListener('pointerdown', unlock);
  };
}
